import { formatAed } from '../lib/money'
import type {
  Adjustment,
  CheckMonthRow,
  CheckResult,
  CommittedInstallment,
  Installment,
  LedgerType,
  LogRow,
  Month,
  MonthRow,
  PurchasePlan,
} from './types'

/**
 * The budget engine.
 *
 * PURE by design: no storage, no network, and no access to the clock. "Today" is always
 * passed in. That is what makes the whole money model testable in milliseconds, and it is
 * why the rules below can be trusted without running the app.
 *
 * The one rule that governs everything:
 *
 *   A purchase is permitted if and only if, after adding its installments to the
 *   timeline, no month's closing balance from the current month through the last
 *   affected month is negative.
 *
 * Rollover, already-committed future installments, and multiple overlapping splits all
 * fall out of that rule. Do not add special cases for them.
 */

export const DEFAULT_ALLOWANCE_FILS = 250_000

export const TIMEZONE = 'Asia/Dubai'

// ------------------------------------------------------------------ months

export function monthOf(date: Date): Month {
  // Always resolved in Asia/Dubai, never the device zone, so travelling with the phone
  // cannot shift which month a purchase lands in.
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: TIMEZONE,
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(date)
  const year = parts.find((p) => p.type === 'year')!.value
  const month = parts.find((p) => p.type === 'month')!.value
  return `${year}-${month}`
}

export function currentMonth(now: Date = new Date()): Month {
  return monthOf(now)
}

export function addMonths(month: Month, count: number): Month {
  const [year, m] = month.split('-').map(Number)
  const zeroBased = (year * 12 + (m - 1)) + count
  const newYear = Math.floor(zeroBased / 12)
  const newMonth = (zeroBased % 12) + 1
  return `${String(newYear).padStart(4, '0')}-${String(newMonth).padStart(2, '0')}`
}

export function compareMonths(a: Month, b: Month): number {
  return a < b ? -1 : a > b ? 1 : 0
}

export function monthRange(first: Month, until: Month): Month[] {
  if (compareMonths(until, first) < 0) return []
  const out: Month[] = []
  let m = first
  while (compareMonths(m, until) <= 0) {
    out.push(m)
    m = addMonths(m, 1)
  }
  return out
}

export function monthLabel(month: Month): string {
  const [year, m] = month.split('-').map(Number)
  const date = new Date(Date.UTC(year, m - 1, 1))
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC',
    month: 'long',
    year: 'numeric',
  }).format(date)
}

export function shortMonthLabel(month: Month): string {
  const [year, m] = month.split('-').map(Number)
  const date = new Date(Date.UTC(year, m - 1, 1))
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC',
    month: 'short',
    year: 'numeric',
  }).format(date)
}

/** Days left before the budget resets on the 1st. */
export function daysUntilReset(now: Date = new Date()): number {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now)
  const year = Number(parts.find((p) => p.type === 'year')!.value)
  const month = Number(parts.find((p) => p.type === 'month')!.value)
  const day = Number(parts.find((p) => p.type === 'day')!.value)
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate()
  return daysInMonth - day + 1
}

// ---------------------------------------------------------------- timeline

export interface TimelineInput {
  firstMonth: Month
  untilMonth: Month
  installments: Installment[]
  adjustments: Map<Month, number>
  allowanceFor: (month: Month) => number
  extraInstallments?: Installment[]
}

/**
 * The derived timeline. Every balance in the app comes from here; nothing is stored.
 *
 * opening(N) === closing(N-1) — that is the rollover. It is unlimited, never expires, and
 * is never clamped at zero: a negative month must stay visible.
 */
export function computeTimeline(input: TimelineInput): MonthRow[] {
  const charges = new Map<Month, number>()
  const all = [...input.installments, ...(input.extraInstallments ?? [])]
  for (const i of all) {
    charges.set(i.dueMonth, (charges.get(i.dueMonth) ?? 0) + i.amountFils)
  }

  let balance = 0
  return monthRange(input.firstMonth, input.untilMonth).map((month) => {
    const opening = balance
    const allowance = input.allowanceFor(month)
    const adjustment = input.adjustments.get(month) ?? 0
    const charge = charges.get(month) ?? 0
    const closing = opening + allowance + adjustment - charge
    balance = closing
    return {
      month,
      openingFils: opening,
      allowanceFils: allowance,
      adjustmentFils: adjustment,
      chargesFils: charge,
      closingFils: closing,
    }
  })
}

/**
 * Splits a price into installments.
 *
 * full   -> one installment in the current month.
 * split4 -> four installments; the last absorbs the rounding remainder so they always sum
 *           to exactly the price.
 */
export function splitInstallments(
  priceFils: number,
  plan: PurchasePlan,
  month: Month,
  purchaseId: string,
  idFactory: (seq: number) => string
): Installment[] {
  if (priceFils <= 0) throw new Error('Price must be positive')

  if (plan === 'full') {
    return [
      { id: idFactory(1), purchaseId, seq: 1, dueMonth: month, amountFils: priceFils },
    ]
  }

  const base = Math.floor(priceFils / 4)
  return [1, 2, 3, 4].map((seq) => ({
    id: idFactory(seq),
    purchaseId,
    seq,
    dueMonth: addMonths(month, seq - 1),
    amountFils: seq < 4 ? base : priceFils - 3 * base,
  }))
}

export interface AffordabilityInput extends Omit<TimelineInput, 'untilMonth' | 'extraInstallments'> {
  currentMonth: Month
  candidate: Installment[]
  plan: PurchasePlan
  priceFils: number
}

/**
 * Runs the affordability rule for a candidate set of installments.
 *
 * `availableBefore` is the month's closing balance *without* the candidate, so it already
 * accounts for rollover and previously committed installments. `closingAfter` is the real
 * closing with the candidate applied, which for a split is cumulative across months.
 */
export function canAfford(input: AffordabilityInput): CheckResult {
  const lastAffected = input.candidate.reduce<Month>(
    (latest, i) => (compareMonths(i.dueMonth, latest) > 0 ? i.dueMonth : latest),
    input.currentMonth
  )
  const until = compareMonths(lastAffected, input.currentMonth) > 0 ? lastAffected : input.currentMonth

  const base = {
    firstMonth: input.firstMonth,
    untilMonth: until,
    installments: input.installments,
    adjustments: input.adjustments,
    allowanceFor: input.allowanceFor,
  }
  const baseline = new Map(computeTimeline(base).map((r) => [r.month, r]))
  const projected = new Map(
    computeTimeline({ ...base, extraInstallments: input.candidate }).map((r) => [r.month, r])
  )

  const months: CheckMonthRow[] = monthRange(input.currentMonth, until).map((month) => {
    const charge = input.candidate
      .filter((i) => i.dueMonth === month)
      .reduce((sum, i) => sum + i.amountFils, 0)
    const after = projected.get(month)?.closingFils ?? 0
    return {
      month,
      availableBeforeFils: baseline.get(month)?.closingFils ?? 0,
      chargeFils: charge,
      closingAfterFils: after,
      ok: after >= 0,
    }
  })

  const firstFailure = months.find((m) => !m.ok)
  return {
    plan: input.plan,
    priceFils: input.priceFils,
    installments: input.candidate,
    ok: !firstFailure,
    months,
    shortfallFils: firstFailure ? -firstFailure.closingAfterFils : 0,
    shortfallMonth: firstFailure ? firstFailure.month : null,
  }
}

// --------------------------------------------------------------------- log

export interface InstallmentContext {
  installment: Installment
  itemTitle: string
  brand: string | null
  plan: PurchasePlan
  totalCount: number
  purchaseTimestamp: string
}

export interface CancellationEvent {
  id: string
  month: Month
  releasedFils: number
  itemTitle: string
  timestamp: string
}

export interface LogInput {
  firstMonth: Month
  untilMonth: Month
  contexts: InstallmentContext[]
  adjustments: Adjustment[]
  cancellations: CancellationEvent[]
  allowanceFor: (month: Month) => number
}

const SORT_RANK: Record<LedgerType, number> = {
  ROLLOVER: 0,
  BUDGET_ADDED: 1,
  ADJUSTMENT: 2,
  EARNED: 2,
  REFUND: 2,
  PURCHASE_FULL: 3,
  PURCHASE_SPLIT: 3,
  INSTALLMENT_DUE: 3,
  PURCHASE_CANCELLED: 4,
}

/**
 * ROLLOVER and PURCHASE_CANCELLED are informational: the amount is shown but the running
 * balance does not move. Rollover is already the previous month's closing, and a
 * cancellation's refund is carried by its own adjustment row.
 */
export function affectsBalance(type: LedgerType): boolean {
  return type !== 'ROLLOVER' && type !== 'PURCHASE_CANCELLED'
}

/**
 * Builds the log.
 *
 * BUDGET_ADDED and ROLLOVER are generated here rather than stored, because they are pure
 * functions of the timeline — storing them would let the database drift from the computed
 * truth. Installment charges are likewise derived, which is why nothing needs to run in
 * the background when a month rolls over.
 *
 * Guarantee (asserted by a test): the running balance of a month's last row equals that
 * month's closing balance from computeTimeline.
 */
export function buildLog(input: LogInput): LogRow[] {
  const out: LogRow[] = []
  let running = 0

  for (const month of monthRange(input.firstMonth, input.untilMonth)) {
    const opening = running

    if (opening !== 0 && month !== input.firstMonth) {
      out.push({
        id: `rollover-${month}`,
        month,
        timestamp: null,
        type: 'ROLLOVER',
        amountFils: opening,
        note: `Rolled over from ${monthLabel(addMonths(month, -1))}`,
        runningBalanceFils: running,
        affectsBalance: false,
      })
    }

    const allowance = input.allowanceFor(month)
    running += allowance
    out.push({
      id: `allowance-${month}`,
      month,
      timestamp: null,
      type: 'BUDGET_ADDED',
      amountFils: allowance,
      note: `Monthly budget added for ${monthLabel(month)}`,
      runningBalanceFils: running,
      affectsBalance: true,
    })

    interface Pending {
      id: string
      type: LedgerType
      amount: number
      note: string
      ts: string | null
    }
    const pending: Pending[] = []

    for (const a of input.adjustments.filter((x) => x.month === month)) {
      const label = a.note && a.note.trim() ? a.note.trim() : null
      pending.push({
        id: a.id,
        type: a.source === 'earned' ? 'EARNED' : a.source === 'refund' ? 'REFUND' : 'ADJUSTMENT',
        amount: a.amountFils,
        note:
          a.source === 'earned'
            ? `Earned "${label ?? 'extra budget'}"`
            : a.source === 'refund'
              ? `Refunded "${label ?? 'a purchase'}"`
              : label
                ? `Budget adjustment: ${label}`
                : 'Manual budget adjustment',
        ts: a.createdAt,
      })
    }

    for (const c of input.contexts.filter((x) => x.installment.dueMonth === month)) {
      const brandPart = c.brand && c.brand.trim() ? ` (${c.brand})` : ''
      let type: LedgerType
      let note: string
      if (c.plan === 'full') {
        type = 'PURCHASE_FULL'
        note = `Bought "${c.itemTitle}"${brandPart} — paid in full`
      } else if (c.installment.seq === 1) {
        type = 'PURCHASE_SPLIT'
        note = `Bought "${c.itemTitle}"${brandPart} — split over ${c.totalCount} months, 1/${c.totalCount}`
      } else {
        type = 'INSTALLMENT_DUE'
        note = `Installment ${c.installment.seq}/${c.totalCount} for "${c.itemTitle}"`
      }
      pending.push({
        id: c.installment.id,
        type,
        amount: -c.installment.amountFils,
        note,
        ts: c.purchaseTimestamp,
      })
    }

    for (const c of input.cancellations.filter((x) => x.month === month)) {
      pending.push({
        id: c.id,
        type: 'PURCHASE_CANCELLED',
        amount: c.releasedFils,
        note: `Cancelled "${c.itemTitle}" — refunded ${formatAed(c.releasedFils)}`,
        ts: c.timestamp,
      })
    }

    pending.sort((a, b) => {
      const rank = SORT_RANK[a.type] - SORT_RANK[b.type]
      if (rank !== 0) return rank
      const ts = (a.ts ?? '').localeCompare(b.ts ?? '')
      if (ts !== 0) return ts
      return a.id.localeCompare(b.id)
    })

    for (const p of pending) {
      const moves = affectsBalance(p.type)
      if (moves) running += p.amount
      out.push({
        id: p.id,
        month,
        timestamp: p.ts,
        type: p.type,
        amountFils: p.amount,
        note: p.note,
        runningBalanceFils: running,
        affectsBalance: moves,
      })
    }
  }

  return out
}

/** Installments committed against future months, i.e. not yet charged. */
export function committedFuture(
  contexts: InstallmentContext[],
  month: Month
): CommittedInstallment[] {
  return contexts
    .filter((c) => compareMonths(c.installment.dueMonth, month) > 0)
    .sort(
      (a, b) =>
        compareMonths(a.installment.dueMonth, b.installment.dueMonth) ||
        a.itemTitle.localeCompare(b.itemTitle)
    )
    .map((c) => ({
      month: c.installment.dueMonth,
      itemTitle: c.itemTitle,
      seq: c.installment.seq,
      totalCount: c.totalCount,
      amountFils: c.installment.amountFils,
    }))
}
